import React from 'react'
import { ActivityIndicator, Linking, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useAdminEvent } from '@hooks'
import { formatAdminDateTime, formatEventPrice } from '@utils'
import type { AdminStackParamList } from '../../../navigation/navigator/AdminStackNavigator'
import { AdminPage } from './components/AdminPage'

type Props = NativeStackScreenProps<AdminStackParamList, 'AdminEventDetail'>

/** One event, read-only: its information, its figures and a link to its public page. */
export const AdminEventDetail: React.FC<Props> = ({ route, navigation }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const { data: event, isLoading, isError } = useAdminEvent(route.params.eventId)
  const goBack = () =>
    navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminEvents')
  const money = (cents: number) => formatEventPrice(cents, currentLanguage, event?.currency)

  return (
    <AdminPage title={t('admin.eventDetail.title')} onBack={goBack} active="events">
      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : isError || !event ? (
        <Text type="bodySmall" color={theme.colors.error} testID="admin-event-error">
          {t('admin.eventDetail.notFound')}
        </Text>
      ) : (
        <>
          <View style={styles.card}>
            <Text type="titleBold" testID="admin-event-name">
              {event.name}
            </Text>
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t(`admin.events.phase.${event.phase}`)}
              {` · ${t('admin.eventDetail.code')} ${event.code}`}
            </Text>
            {event.description ? <Text type="body">{event.description}</Text> : null}
            <Button
              testID="admin-event-public-link"
              title={t('admin.eventDetail.publicPage')}
              unfilled
              isSmall
              onPress={() => Linking.openURL(event.publicUrl)}
            />
          </View>

          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.eventDetail.figures')}</Text>
            <View style={styles.figures}>
              <Figure label={t('admin.events.attendees')} value={String(event.attendeeCount)} testID="admin-event-attendees" />
              <Figure label={t('admin.events.checkedIn')} value={String(event.checkedInCount)} testID="admin-event-checkedin" />
              <Figure
                label={t('admin.eventDetail.capacity')}
                value={event.capacity === null ? t('admin.eventDetail.unlimited') : String(event.capacity)}
              />
              <Figure
                label={t('admin.events.price')}
                value={event.priceCents === null ? t('admin.events.free') : money(event.priceCents)}
              />
              {event.priceCents !== null ? (
                <>
                  <Figure label={t('admin.events.paidTotal')} value={money(event.paidCents)} testID="admin-event-paid" />
                  <Figure label={t('admin.overview.commission')} value={money(event.commissionCents)} />
                  <Figure label={t('admin.overview.net')} value={money(event.organizerNetCents)} />
                  <Figure
                    label={t('admin.eventDetail.payments')}
                    value={String(event.paymentsCount)}
                  />
                </>
              ) : null}
            </View>
            {event.priceCents !== null ? (
              <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                {t('admin.overview.paidOnly')}
              </Text>
            ) : null}
          </View>

          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.eventDetail.information')}</Text>
            <Line label={t('admin.eventDetail.organizer')}>
              {event.organizer ? (
                <TouchableOpacity
                  accessibilityRole="link"
                  testID="admin-event-organizer-link"
                  onPress={() =>
                    navigation.navigate('AdminOrganizerDetail', { accountId: event.organizer!.accountId })
                  }
                >
                  <Text type="body" color={theme.colors.primary}>
                    {event.organizer.name || event.organizer.email}
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text type="body">{t('admin.events.noOrganizer')}</Text>
              )}
            </Line>
            <Line label={t('admin.eventDetail.starts')}>
              <Text type="body">{formatAdminDateTime(event.startsAt, currentLanguage)}</Text>
            </Line>
            <Line label={t('admin.eventDetail.ends')}>
              <Text type="body">{formatAdminDateTime(event.endsAt, currentLanguage)}</Text>
            </Line>
            <Line label={t('admin.eventDetail.registrationCloses')}>
              <Text type="body">{formatAdminDateTime(event.registrationClosesAt, currentLanguage)}</Text>
            </Line>
            <Line label={t('admin.eventDetail.format')}>
              <Text type="body">{t(`admin.eventDetail.formats.${event.format}`)}</Text>
            </Line>
            {event.location || event.address || event.city ? (
              <Line label={t('admin.eventDetail.place')}>
                <Text type="body">
                  {[event.location, event.address, event.city].filter(Boolean).join(', ')}
                </Text>
              </Line>
            ) : null}
            {event.onlineUrl ? (
              <Line label={t('admin.eventDetail.onlineUrl')}>
                <Text type="body">{event.onlineUrl}</Text>
              </Line>
            ) : null}
            {event.contactEmail ? (
              <Line label={t('admin.eventDetail.contact')}>
                <Text type="body">{event.contactEmail}</Text>
              </Line>
            ) : null}
            <Line label={t('admin.eventDetail.createdOn')}>
              <Text type="body">{formatAdminDateTime(event.createdAt, currentLanguage)}</Text>
            </Line>
          </View>
        </>
      )}
    </AdminPage>
  )
}

const Figure: React.FC<{ label: string; value: string; testID?: string }> = ({
  label,
  value,
  testID,
}) => {
  const theme = useTheme()
  return (
    <View style={{ minWidth: 110, flexGrow: 1, flexBasis: 110 }}>
      <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
        {label}
      </Text>
      <Text type="subtitleBold" testID={testID}>
        {value}
      </Text>
    </View>
  )
}

const Line: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const theme = useTheme()
  return (
    <View style={{ gap: 2 }}>
      <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
        {label}
      </Text>
      {children}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    figures: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.md },
  })
